import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import MaterialTable from 'material-table';
import Container from '@material-ui/core/Container';
import SvgIcon from '@material-ui/core/SvgIcon';
import Switch from '@material-ui/core/Switch';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { LabelerTemplate } from '../../models';
import { PaginatedResponse } from '../../../shared/models';
import { checkStatus, parseJSON, parseText, handleError } from '../../../shared/api/core/common';
import { getTemplatesByPage, getTemplateById, updateTemplate, deleteTemplate } from '../../api/Api';
import { API_URLS } from '../../api/ApiUrls';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { CustomTableTitle, ErrorStatusBar } from '../../../shared/components';
import { Toast, enqueueSnackbar, ToastSuccess, ToastError } from '../../../shared/components/Notifier';
import { hasRequiredPermissions } from '../../../shared/lib/PermissionManager';
import { EMPTY_ROW_MSG, ERROR_MSG, ERROR_500 } from '../../../shared/lib/Localization';
import { initQueryParams, replaceHistory, tableOptions, useQuery } from '../../../shared/lib/TableQueryParams';
import composeRequest from '../../../shared/api/core';
import { ZPLUtil } from '../../utils/ZPLConverter';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    }),
);

const screenPermissions = {
    'add': false,
    'change': false,
    'delete': false,
    'view': false
};

const TemplateListScreen = (props: any) => {
    const { userData } = props.userData.userData;
    const classes = useStyles({});
    const isSuperuser = props.userData?.userData?.userData?.is_superuser === true;
    const [emptyDataSourceMessage, setEmptyDataSourceMessage] = useState(EMPTY_ROW_MSG);
    const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
    const [includeInactive, setIncludeInactive] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<LabelerTemplate | null>(null);
    const [editorContent, setEditorContent] = useState('');
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const tableRef = useRef<{ onQueryChange: any; }>();
    let locationQuery = useQuery(useLocation);
    const [tableQuery, setTableQuery] = useState(initQueryParams(locationQuery));
    const [isLoading, setLoading] = useState(false);
    const [permissions, setPermissions] = useState(screenPermissions as any);

    useEffect(() => {
        if (userData && userData.user_permissions) {
            const permissions = [...userData.user_permissions];
            const availablePermissions = hasRequiredPermissions(permissions,
                'labelertemplate', screenPermissions);
            setPermissions(availablePermissions);
        }
    }, [userData && userData.user_permissions]);

    useEffect(() => {
        if (!isLoading) {
            tableRef.current && tableRef.current.onQueryChange();
        }
    }, [props.userData.selectedBusiness]);

    useEffect(() => {
        if (!isLoading && isSuperuser) {
            tableRef.current && tableRef.current.onQueryChange();
        }
    }, [includeInactive]);

    const openEditModal = (rowData: LabelerTemplate) => {
        setEditingTemplate(rowData);
        setEditName(rowData.name || '');
        setEditDescription(rowData.description || '');
        setEditorContent('');
        setEditModalOpen(true);
        getTemplateById(rowData.id)
            .then(checkStatus)
            .then(parseJSON)
            .then((template: LabelerTemplate) => {
                setEditingTemplate(template);
                setEditName(template.name || '');
                setEditDescription(template.description || '');
                setEditorContent(template.content ? JSON.stringify(template.content, null, 2) : '{}');
            })
            .catch(() => {
                props.showToast({ message: 'Failed to load template.', options: ToastError });
            });
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditingTemplate(null);
        setEditorContent('');
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleSaveEdit = () => {
        if (!editingTemplate) return;
        try {
            const content = editorContent ? JSON.parse(editorContent) : null;
            setIsSaving(true);
            updateTemplate(editingTemplate.id, {
                name: editName,
                description: editDescription,
                content
            })
                .then(checkStatus)
                .then(parseJSON)
                .then(() => {
                    props.showToast({ message: 'Template updated successfully!', options: ToastSuccess });
                    closeEditModal();
                })
                .catch(() => {
                    props.showToast({ message: 'Failed to update template.', options: ToastError });
                })
                .finally(() => setIsSaving(false));
        } catch (e) {
            props.showToast({ message: 'Invalid JSON in content.', options: ToastError });
        }
    };

    const handlePreview = () => {
        try {
            const parsed = editorContent ? JSON.parse(editorContent) : null;
            if (!parsed) return;
            const zpl = ZPLUtil.convertJSONToZPL(parsed);
            composeRequest(API_URLS.labelerZPLPreviewUrl, 'POST', JSON.stringify({ data: zpl }), 'application/json')
                .then(checkStatus)
                .then(parseText)
                .then((base64: string) => {
                    const w = window.open('');
                    if (w) {
                        w.document.write(`<img src="data:image/png;base64,${base64}" />`);
                    }
                })
                .catch(() => props.showToast({ message: 'Preview failed.', options: ToastError }));
        } catch (e) {
            props.showToast({ message: 'Invalid JSON for preview.', options: ToastError });
        }
    };

    return (
        <Container maxWidth="xl">
            <ErrorStatusBar isVisible={isErrorStatusVisible} />
            <div className="table-wrapper">
                <MaterialTable
                    options={{ 
                        ...tableOptions(tableQuery),
                        thirdSortClick: false,
                        draggable: false,
                        sorting: true 
                    }}
                    tableRef={tableRef}
                    localization={{
                        body: {
                            emptyDataSourceMessage: emptyDataSourceMessage
                        }
                    }}
                    columns={[
                        { title: 'ID', field: 'id' },
                        { title: 'Name', field: 'name' },
                        {
                            title: 'Description',
                            field: 'description',
                        },
                        {
                            title: 'Active',
                            field: 'active',
                            type: 'boolean',
                            sorting: false,
                            render: rowData => <Switch
                                checked={rowData.active}
                                // onClick={() => handleSwitchChange('active',rowData)}
                                inputProps={{ 'aria-label': 'secondary checkbox' }}
                            />
                        },
                        {
                            title: 'Last Modified',
                            field: 'modified_date',
                            render: rowData => (
                                rowData.modified_date ?
                                    <div>{moment(rowData.modified_date).format('YYYY-MM-DD HH:mm')}</div>
                                    : <div>Never</div>
                            ),
                        },
                        {
                            title: 'Test',
                            field: 'is_test',
                            type: 'boolean',
                            sorting: false,
                            render: rowData => <Switch
                                checked={rowData.is_test}
                                // onClick={() => handleSwitchChange('active',rowData)}
                                inputProps={{ 'aria-label': 'secondary checkbox' }}
                            />
                        },
                        // { 
                        //     title: 'Created On',
                        //     field: 'created_date',
                        //     type: 'string',
                        //     render: rowData => (
                        //         rowData.modified_date ? 
                        //         <div>{moment(rowData.modified_date).format('YYYY-MM-DD HH:mm')}</div>
                        //         : <div></div>
                        //     ),
                        // },
                    ]}
                    data={query =>
                        new Promise((resolve, reject) => {
                            if (!isLoading) {
                                const sb = props.userData.selectedBusiness;
                                if (sb && sb.business_id) {
                                    let pageData = {
                                        data: [] as LabelerTemplate[],
                                        totalCount: 0,
                                        page: query.page
                                    };
                                    setLoading(true);
                                    let orderBy = '';
                                    if (query.orderBy) {
                                        orderBy = query.orderBy.field as string;
                                        orderBy = `${query.orderDirection && query.orderDirection === 'desc' ? '-' : ''}${orderBy}`;
                                    }
                                    getTemplatesByPage(
                                        query.page + 1, 
                                        query.pageSize,
                                        query.search, 
                                        sb.business_id,
                                        orderBy,
                                        isSuperuser ? includeInactive : false,
                                    )
                                        .catch(handleError) // handle network issues
                                        .then(checkStatus)
                                        .then(parseJSON)
                                        .then((data: PaginatedResponse<LabelerTemplate>) => {
                                            setErrorStatusVisible(false);
                                            setEmptyDataSourceMessage(EMPTY_ROW_MSG);
                                            pageData = {
                                                data: data.results,
                                                totalCount: data.count,
                                                page: query.page
                                            };
                                            setTableQuery({
                                                ...tableQuery,
                                                orderBy,
                                                totalCount: data.count,
                                                page: query.page,
                                                pageSize: query.pageSize
                                            });
                                            setLoading(false);
                                            resolve(pageData);
                                        })
                                        .catch((error: any) => {
                                            if (error) {
                                                if (error.status && error.status === 500) {
                                                    setEmptyDataSourceMessage(ERROR_500);
                                                    setErrorStatusVisible(false);
                                                } else if ((error.status === 403 || error.status === 401)) {
                                                    setEmptyDataSourceMessage(ERROR_MSG);
                                                    setErrorStatusVisible(true);
                                                } else {
                                                    setEmptyDataSourceMessage(ERROR_MSG);
                                                    setErrorStatusVisible(false);
                                                }
                                            }
                                            setTableQuery({
                                                ...tableQuery,
                                                totalCount: 0,
                                                page: query.page,
                                                pageSize: query.pageSize
                                            });
                                            setLoading(false);
                                            resolve(pageData);
                                        });
                                }
                            }
                        })
                    }
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                            <CustomTableTitle
                                history={props.history}
                                title={'Label Templates'}
                                icon={SVG_ICONS.template} />
                            {isSuperuser && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeInactive}
                                            onChange={(e) => setIncludeInactive(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Include inactive"
                                />
                            )}
                        </div>
                    }
                    actions={[
                        {
                            icon: 'refresh',
                            tooltip: 'Refresh Data',
                            isFreeAction: true,
                            onClick: () => tableRef.current && tableRef.current.onQueryChange(),
                        },
                        {
                            icon: 'add_box',
                            tooltip: 'Add Template',
                            isFreeAction: true,
                            onClick: () => { },
                        },
                        {
                            icon: 'edit',
                            tooltip: 'Edit',
                            onClick: (event, rowData) => rowData && openEditModal(rowData)
                        },
                        {
                            icon: () => <SvgIcon><path d={SVG_ICONS.copy} /></SvgIcon>,
                            tooltip: 'Copy',
                            onClick: (event, rowData) => { },
                        }
                    ]}
                    editable={{
                        onRowDelete: oldData =>
                            new Promise<void>((resolve, reject) => {
                                setTimeout(() => {
                                    deleteTemplate(oldData)
                                        .catch(handleError) // handle network issues
                                        .then(checkStatus)
                                        .then(parseJSON)
                                        .then(jsonData => {
                                            props.showToast({
                                                message: 'Template deleted successfully!',
                                                options: ToastSuccess
                                            });
                                        })
                                        .catch((error: any) => {
                                            props.showToast({
                                                message: 'Failed to delete template.',
                                                options: ToastError
                                            });
                                            if (error) {
                                                if (error.status && error.status === 500) {
                                                    setEmptyDataSourceMessage(ERROR_500);
                                                    setErrorStatusVisible(false);
                                                } else if ((error.status === 403 || error.status === 401)
                                                ) {
                                                    setEmptyDataSourceMessage(ERROR_MSG);
                                                    setErrorStatusVisible(true);
                                                } else {
                                                    setEmptyDataSourceMessage(ERROR_MSG);
                                                    setErrorStatusVisible(false);
                                                }
                                            }
                                        });
                                    resolve();
                                }, 1000);
                            }),
                    }}
                />
            </div>
            <Dialog open={editModalOpen} onClose={closeEditModal} maxWidth="md" fullWidth>
                <DialogTitle>Edit Template {editingTemplate?.name && `- ${editingTemplate.name}`}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Description"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <div style={{ marginTop: 16 }}>
                        <strong>Content (JSON)</strong>
                        <div style={{ marginTop: 8, border: '1px solid #ccc', borderRadius: 4, overflow: 'auto', maxHeight: 300 }}>
                            <Editor
                                value={editorContent}
                                onValueChange={setEditorContent}
                                highlight={(code) => Prism.highlight(code, Prism.languages.json, 'json')}
                                padding={10}
                                style={{ fontFamily: 'monospace', fontSize: 14, minHeight: 280 }}
                            />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePreview} color="primary" disabled={isSaving}>
                        Preview
                    </Button>
                    <Button onClick={closeEditModal}>Cancel</Button>
                    <Button onClick={handleSaveEdit} color="primary" variant="contained" disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

const mapStateToProps = (state: any) => {
    return {
        userData: state.userLocalData,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TemplateListScreen);